// src/components/chat/ChatModal.js
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ChatInbox from './ChatInbox';
import ChatConversation from './ChatConversation';
import { injectStyles, THEME_KEY } from '../../shared/PostCard';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const MODAL_CSS = `
.mfc-root{position:fixed;inset:0;z-index:10050;display:flex;justify-content:center;background:var(--mf-bg);color:var(--mf-text)}
.mfc-shell{position:relative;display:flex;width:100%;height:100%;min-height:0;overflow:hidden;background:var(--mf-bg)}
.mfc-side{display:flex;flex-direction:column;width:390px;flex:none;min-width:0;min-height:0;background:var(--mf-surface);border-right:1px solid var(--mf-border)}
.mfc-main{position:relative;flex:1;min-width:0;min-height:0;display:flex;flex-direction:column}
.mfc-main>*{flex:1;min-height:0}

.mfc-placeholder{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:6px;padding:24px;color:var(--mf-muted);
  background:radial-gradient(700px 320px at 50% 30%,var(--mf-glow),transparent 70%),var(--mf-bg)}
.mfc-placeholder .ico{width:84px;height:84px;margin-bottom:10px;border-radius:28px;display:grid;place-items:center;background:var(--mf-accent-soft);color:var(--mf-accent);font-size:40px}
.mfc-placeholder h3{margin:0;font-size:20px;color:var(--mf-text)}
.mfc-placeholder p{margin:0;max-width:300px;font-size:14px;line-height:1.5}
.mfc-placeholder .lock{display:inline-flex;align-items:center;gap:6px;margin-top:14px;padding:6px 12px;border-radius:999px;background:var(--mf-surface-2);
  border:1px solid var(--mf-border);font-size:12px;font-weight:600;color:var(--mf-faint)}
.mfc-placeholder .lock svg{font-size:14px}

/* desktop + tablet landscape: floating two-pane window */
@media (min-width:861px){
  .mfc-root{padding:clamp(12px,3vh,32px) clamp(12px,3vw,40px);background:var(--mf-overlay)}
  .mfc-shell{max-width:1240px;border-radius:26px;border:1px solid var(--mf-border-strong);box-shadow:0 30px 90px rgba(0,0,0,.5)}
}

/* phones + small tablets: one pane at a time, full screen (keyboard-aware) */
@media (max-width:860px){
  .mfc-root{inset:auto;left:0;right:0;top:var(--mfc-top,0px);height:var(--mfc-vh,100dvh)}
  .mfc-side{width:100%;border-right:0}
  .mfc-shell.has-chat .mfc-side{display:none}
  .mfc-shell:not(.has-chat) .mfc-main{display:none}
}
`;
injectStyles('mfc-modal-css', MODAL_CSS);

/* ------------------------------------------------------------------ */
/* Theme: follow the feed's dark / light switch                         */
/* ------------------------------------------------------------------ */
const FEED_SELECTOR = '.mf-feed[data-mf-theme]:not(.mfc-root)';

function detectTheme() {
  try {
    const el = document.querySelector(FEED_SELECTOR);
    const t = el && el.getAttribute('data-mf-theme');
    if (t === 'light' || t === 'dark') return t;
  } catch {}
  try {
    const t = localStorage.getItem(THEME_KEY);
    if (t === 'light' || t === 'dark') return t;
  } catch {}
  return 'dark';
}

function useChatTheme() {
  const [theme, setTheme] = useState(detectTheme);

  useEffect(() => {
    const el = document.querySelector(FEED_SELECTOR);
    if (!el || typeof MutationObserver === 'undefined') return undefined;
    const mo = new MutationObserver(() => setTheme(detectTheme()));
    mo.observe(el, { attributes: true, attributeFilter: ['data-mf-theme'] });
    return () => mo.disconnect();
  }, []);

  return theme;
}

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */
export default function ChatModal({ onClose }) {
  const theme = useChatTheme();
  const rootRef = useRef(null);
  const [selected, setSelected] = useState(null);

  // freeze the page behind the chat
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // on phones, follow the visible area so the composer stays above the keyboard
  useEffect(() => {
    const vv = window.visualViewport;
    const root = rootRef.current;
    if (!vv || !root) return undefined;

    const apply = () => {
      root.style.setProperty('--mfc-vh', `${vv.height}px`);
      root.style.setProperty('--mfc-top', `${vv.offsetTop}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={rootRef}
      className="mf-feed mfc-root"
      data-mf-theme={theme}
      role="dialog"
      aria-modal="true"
      aria-label="Messages"
      onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <div className={`mfc-shell${selected ? ' has-chat' : ''}`}>
        <aside className="mfc-side">
          <ChatInbox
            onSelect={setSelected}
            selectedFriendId={selected ? selected.id : null}
            onClose={onClose}
          />
        </aside>

        <section className="mfc-main">
          {selected ? (
            <ChatConversation
              key={selected.id}
              userId={selected.id}
              friend={selected}
              onBack={() => setSelected(null)}
            />
          ) : (
            <div className="mfc-placeholder">
              <div className="ico"><ForumRoundedIcon fontSize="inherit" /></div>
              <h3>Your messages</h3>
              <p>Pick a chat on the left to start talking. Everything you send is end-to-end encrypted.</p>
              <span className="lock"><LockRoundedIcon /> Only you and your friend can read this</span>
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body
  );
}